import React, { useState, useEffect } from 'react';
import { X, Plus, Search, Check, Edit2, Trash2, ChevronDown } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { 
  WorkflowStepLibraryItem,
  getWorkflowStepLibrary,
  createWorkflowStepLibraryItem,
  updateWorkflowStepLibraryItem,
  deleteWorkflowStepLibraryItem,
  initializeDefaultWorkflowSteps
} from '../lib/workflowStepLibrary';

interface WorkflowStepLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectStep: (step: { id: string; title: string; description?: string; category?: string }) => void;
}

export default function WorkflowStepLibraryModal({
  isOpen,
  onClose,
  onSelectStep
}: WorkflowStepLibraryModalProps) {
  const { selectedBusiness } = useBusiness();
  const [librarySteps, setLibrarySteps] = useState<WorkflowStepLibraryItem[]>([]);
  const [filteredSteps, setFilteredSteps] = useState<WorkflowStepLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [editingStep, setEditingStep] = useState<string | null>(null);
  
  // New step form
  const [newStepTitle, setNewStepTitle] = useState('');
  const [newStepDescription, setNewStepDescription] = useState('');
  const [newStepCategory, setNewStepCategory] = useState('General');
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [tempNewCategory, setTempNewCategory] = useState('');

  useEffect(() => {
    if (isOpen && selectedBusiness?.id) {
      loadLibrarySteps();
    }
  }, [isOpen, selectedBusiness?.id]);

  useEffect(() => {
    filterSteps();
  }, [librarySteps, searchTerm, selectedCategory]);

  const loadLibrarySteps = async () => {
    if (!selectedBusiness?.id) return;

    setLoading(true);
    try {
      // Initialize default steps if needed
      await initializeDefaultWorkflowSteps(selectedBusiness.id);
      
      // Load library steps
      const steps = await getWorkflowStepLibrary(selectedBusiness.id);
      setLibrarySteps(steps);
    } catch (error) {
      console.error('Error loading workflow step library:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSteps = () => {
    let filtered = librarySteps;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(step =>
        step.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (step.description && step.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(step => step.category === selectedCategory);
    }

    setFilteredSteps(filtered);
  };

  const getCategories = () => {
    const categories = new Set(librarySteps.map(step => step.category).filter(Boolean));
    return ['all', ...Array.from(categories)];
  };

  const getUniqueCategories = () => {
    const categories = new Set(librarySteps.map(step => step.category).filter(Boolean));
    // Always include 'General' as a default category
    categories.add('General');
    return Array.from(categories).sort();
  };

  const handleCreateStep = async () => {
    if (!selectedBusiness?.id || !newStepTitle.trim()) return;

    try {
      const newStep = await createWorkflowStepLibraryItem(
        selectedBusiness.id,
        newStepTitle.trim(),
        newStepDescription.trim() || undefined,
        newStepCategory
      );
      
      setLibrarySteps([...librarySteps, newStep]);
      
      // Reset form
      setNewStepTitle('');
      setNewStepDescription('');
      setNewStepCategory('General');
      setIsCreating(false);
      setIsAddingNewCategory(false);
      setTempNewCategory('');
      
      // Optionally select the newly created step
      onSelectStep({ 
        id: newStep.id, 
        title: newStep.title, 
        description: newStep.description || undefined,
        category: newStep.category || undefined 
      });
      onClose();
    } catch (error) {
      console.error('Error creating workflow step:', error);
    }
  };

  const handleUpdateStep = async (step: WorkflowStepLibraryItem) => {
    try {
      await updateWorkflowStepLibraryItem(step.id, {
        title: step.title,
        description: step.description || undefined,
        category: step.category || undefined
      });
      
      setLibrarySteps(librarySteps.map(s => s.id === step.id ? step : s));
      setEditingStep(null);
    } catch (error) {
      console.error('Error updating workflow step:', error);
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!confirm('Are you sure you want to delete this step from the library?')) return;

    try {
      await deleteWorkflowStepLibraryItem(stepId);
      setLibrarySteps(librarySteps.filter(s => s.id !== stepId));
    } catch (error) {
      console.error('Error deleting workflow step:', error);
    }
  };

  // Component is conditionally rendered by parent, so always render when mounted

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Workflow Step Library</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-col h-[calc(90vh-80px)]">
          {/* Search and Filters */}
          <div className="px-6 py-4 border-b border-gray-200 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search steps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-2 overflow-x-auto">
              {getCategories().map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All Steps' : category}
                </button>
              ))}
            </div>
          </div>

          {/* Steps List or Create Form */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">Loading steps...</div>
              </div>
            ) : isCreating ? (
              /* Create New Step Form */
              <div className="max-w-2xl mx-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Step</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Step Title *
                    </label>
                    <input
                      type="text"
                      value={newStepTitle}
                      onChange={(e) => setNewStepTitle(e.target.value)}
                      placeholder="e.g., Initial Inspection"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      value={newStepDescription}
                      onChange={(e) => setNewStepDescription(e.target.value)}
                      placeholder="Provide additional details about this step..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    {isAddingNewCategory ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={tempNewCategory}
                          onChange={(e) => setTempNewCategory(e.target.value)}
                          placeholder="Enter new category name"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (tempNewCategory.trim()) {
                              setNewStepCategory(tempNewCategory.trim());
                              setTempNewCategory('');
                              setIsAddingNewCategory(false);
                            }
                          }}
                          className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setTempNewCategory('');
                            setIsAddingNewCategory(false);
                          }}
                          className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <select
                          value={newStepCategory}
                          onChange={(e) => {
                            if (e.target.value === 'add-new') {
                              setIsAddingNewCategory(true);
                            } else {
                              setNewStepCategory(e.target.value);
                            }
                          }}
                          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          {getUniqueCategories().map(category => (
                            <option key={category} value={category}>
                              {category}
                            </option>
                          ))}
                          <option value="add-new" className="text-purple-600">
                            + Add new category...
                          </option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => {
                        setIsCreating(false);
                        setNewStepTitle('');
                        setNewStepDescription('');
                        setNewStepCategory('General');
                        setIsAddingNewCategory(false);
                        setTempNewCategory('');
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateStep}
                      disabled={!newStepTitle.trim()}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Create Step
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Steps List */
              <div className="space-y-2">
                {filteredSteps.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">
                      {searchTerm || selectedCategory !== 'all' 
                        ? 'No steps found matching your criteria' 
                        : 'Your workflow step library is empty.'}
                    </p>
                    {!searchTerm && selectedCategory === 'all' && (
                      <>
                        <p className="text-sm text-gray-400 mb-4">
                          Create custom workflow steps that can be reused across all your services.
                        </p>
                        <button
                          onClick={() => setIsCreating(true)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-purple-600 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Create Your First Step
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  filteredSteps.map(step => (
                    <div
                      key={step.id}
                      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {editingStep === step.id ? (
                        /* Edit Mode */
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={step.title}
                            onChange={(e) => setLibrarySteps(librarySteps.map(s => 
                              s.id === step.id ? { ...s, title: e.target.value } : s
                            ))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                          <textarea
                            value={step.description || ''}
                            onChange={(e) => setLibrarySteps(librarySteps.map(s => 
                              s.id === step.id ? { ...s, description: e.target.value } : s
                            ))}
                            placeholder="Description (optional)"
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setEditingStep(null)}
                              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleUpdateStep(step)}
                              className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-medium text-gray-900">{step.title}</h4>
                              {step.category && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded-full">
                                  {step.category}
                                </span>
                              )}
                            </div>
                            {step.description && (
                              <p className="mt-1 text-sm text-gray-600">{step.description}</p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => setEditingStep(step.id)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                              title="Edit step"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteStep(step.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete step"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                onSelectStep({ 
                                  id: step.id, 
                                  title: step.title, 
                                  description: step.description || undefined,
                                  category: step.category || undefined
                                });
                                onClose();
                              }}
                              className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Select
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => setIsCreating(true)}
              disabled={isCreating}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create New Step
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}